import { FC } from "react";
import { observer } from "mobx-react";
import { TMessage } from "@/i18n/tt";
import { formatOrgNum } from "@/utils/other";
import { IconButton, LinkButton } from "@/components-v2/Button";
import { YesNoQuestion } from "@/dbot/common/commonComponents";
import {
  EditCompaniesState,
  GroupContributionByGeneralMeetingForm,
  GroupContributionCompany,
} from "./groupContributionByGeneralMeetingDomain";
import { Alert } from "@/components-v2/Alert";
import { Dialog } from "@/components-v2/Dialog";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";
import { Autocomplete } from "@/components-v2/Autocomplete";
import { MTextInput, TextInput } from "@/components-v2/TextInput";
import { MSelect } from "@/components-v2/Select";
import { noop } from "lodash";
import { CURRENCY_NOK } from "@/utils/currency";
import { Table } from "@/components-v2/Table";
import { GroupMethod } from "@/types/Transactions";
import { when } from "@/utils/functions";

import { useTranslation } from "@/stores/RootStoreContext";
import { NumberFieldModel, RadioFieldModel } from "@/common/commonForms";

const ContributionCompanyDialogForm = observer(
  ({
    disableCompanySearch,
    suggestedCompanies,
    selectedCompany,
    searchInput,
    setSearchInput,
    companyError,
    howToSettle,
    value,
    select,
  }: {
    disableCompanySearch?: boolean;
    suggestedCompanies: {
      name: string;
      organizationnumber: string;
    }[];
    selectedCompany?: {
      name: string;
      organizationnumber: string;
    };
    searchInput: string;
    companyError?: TMessage;
    howToSettle: RadioFieldModel<GroupMethod>;
    value: NumberFieldModel;
    select?: (c: { name: string; organizationnumber: string }) => void;
    setSearchInput?: (input: string) => void;
  }) => {
    const { t } = useTranslation();

    return (
      <VBox gap={"md"}>
        <Grid cols={2}>
          <GridItem xs={2} md={1}>
            <Autocomplete
              fullWidth
              name="groupContributionByGMSearch"
              label={t("decisions.dividend.groupContribution.company")}
              placeholder={t("decisions.dividend.groupContribution.search-company-placeholder")}
              disabled={disableCompanySearch}
              value={selectedCompany}
              items={suggestedCompanies ?? []}
              onSelect={select ?? noop}
              onInputValueChange={setSearchInput ?? noop}
              idFn={(c) => c.organizationnumber}
              labelFn={(c) => c.name}
              inputValue={searchInput}
              error={Boolean(companyError)}
              message={when(companyError, t)}
            />
          </GridItem>
          <GridItem xs={2} md={1}>
            <TextInput
              disabled
              label={t("decisions.dividend.groupContribution.organizationnumber")}
              value={formatOrgNum(selectedCompany?.organizationnumber)}
              name="groupContributionByGMOrgNo"
            />
          </GridItem>
          <GridItem xs={2} md={1}>
            <MSelect
              label={t("decisions.dividend.groupContribution.howToSettle.title")}
              fullWidth
              model={howToSettle}
              name="groupContributionByGMHowToSettle"
            />
          </GridItem>
          <GridItem xs={2} md={1}>
            <MTextInput
              label={t("decisions.enter-amount")}
              prefix={CURRENCY_NOK}
              model={value}
              name={"groupContributionByGMAmount"}
            />
          </GridItem>
        </Grid>
      </VBox>
    );
  },
);

const GroupContributionTable: FC<{
  companies: GroupContributionCompany[];
  isProjectLocked?: boolean;
  edit: (company: GroupContributionCompany) => void;
  remove: (company: GroupContributionCompany) => void;
}> = ({ companies, isProjectLocked, edit, remove }) => {
  const { t } = useTranslation();

  if (companies.length === 0) {
    return null;
  }

  return (
    <div>
      <Table variant="IN_CARD_DENSE" withRowBorders>
        <thead>
          <tr>
            <th style={{ whiteSpace: "nowrap", width: "1%" }} align="left">
              {t("decisions.dividend.groupContribution.company")}
            </th>
            <th style={{ whiteSpace: "nowrap", width: "1%" }} align="left">
              {t("decisions.dividend.groupContribution.organizationnumber")}
            </th>
            <th style={{ whiteSpace: "nowrap" }} align="left">
              {t("decisions.dividend.groupContribution.howToSettle")}
            </th>
            <th style={{ whiteSpace: "nowrap", width: "1%" }} align="right" />
          </tr>
        </thead>
        <tbody>
          {companies.map((company) => (
            <tr key={company.organizationnumber}>
              <td style={{ verticalAlign: "top" }}>{company.name}</td>
              <td style={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                {formatOrgNum(company.organizationnumber)}
              </td>
              <td style={{ verticalAlign: "top" }}>
                {t(`decisions.dividend.groupContribution.howToSettle.${company.howToSettle}`)}
              </td>
              <td style={{ verticalAlign: "top" }}>
                <Box gap={false}>
                  <IconButton
                    icon={"edit"}
                    tooltip={t("edit")}
                    onClick={() => edit(company)}
                    disabled={isProjectLocked}
                  />
                  <IconButton
                    icon={"x"}
                    tooltip={t("remove")}
                    onClick={() => remove(company)}
                    disabled={isProjectLocked}
                  />
                </Box>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const EditCompanyDialog = observer(function EditCompanyDialog({
  editCompaniesState,
  submit,
  cancel,
}: {
  editCompaniesState?: EditCompaniesState;
  submit: () => void;
  cancel: () => void;
}) {
  const { t } = useTranslation();
  switch (editCompaniesState?.type) {
    case "add":
      return (
        <Dialog
          title={t("decisions.dividend.groupContribution.add-company")}
          onSubmit={submit}
          onCancel={cancel}
        >
          <ContributionCompanyDialogForm
            {...editCompaniesState.form}
            setSearchInput={editCompaniesState.form.setSearchInput}
            select={editCompaniesState.form.select}
          />
        </Dialog>
      );
    case "edit":
      return (
        <Dialog
          title={t("decisions.dividend.groupContribution.edit-company")}
          onSubmit={submit}
          onCancel={cancel}
        >
          <ContributionCompanyDialogForm {...editCompaniesState.form} disableCompanySearch />
        </Dialog>
      );
    case "remove":
      return (
        <Dialog
          title={t("decisions.dividend.groupContribution.remove-company-title")}
          onSubmit={submit}
          onCancel={cancel}
        >
          <p>{t("decisions.dividend.groupContribution.remove-company-question")}</p>
        </Dialog>
      );
    default:
      return null;
  }
});

export const GroupContributionByGeneralMeetingView: FC<{
  form: GroupContributionByGeneralMeetingForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <YesNoQuestion
        question={t("decisions.dividend.groupContribution.usePowerOfAttorney")}
        field={form.usePowerOfAttorney}
        onChange={(e) => form.setUsePowerOfAttorney(e)}
        isLocked={form.isProjectLocked}
      />
      <VBox>
        <div>{t("decisions.dividend.groupContribution.title")}</div>
        <GroupContributionTable
          companies={form.companies}
          isProjectLocked={form.isProjectLocked}
          edit={form.openEditCompanyDialog}
          remove={form.openConfirmRemoveDialog}
        />
        <div>
          <LinkButton
            label={t("decisions.dividend.groupContribution.add-company")}
            disabled={form.isProjectLocked}
            onClick={form.openAddCompanyDialog}
            icon={"plus"}
          />
        </div>
        {form.validationError && <Alert type="error" message={t(form.validationError)} />}
      </VBox>
      <EditCompanyDialog
        editCompaniesState={form.editCompaniesState}
        submit={form.submitEditCompaniesState}
        cancel={form.clearEditCompaniesState}
      />
    </VBox>
  );
});
